import { useMemo, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { SalesforceBundle, SalesforceBundleGroup } from "~/api/models/Bundle";
import { useFetchOpportunity } from "~/api/salesForce/useFetchOpportunity";
import SpinnerIcon from "~/components/atoms/SpinnerIcon/SpinnerIcon";
import TabsToggle from "~/components/molecules/TabsToggle/TabsToggle";
import BundleCard from "~/components/organisms/BundleCard/BundleCard";
import { Header } from "~/components/organisms/layout/Header/Header";
import { useBundles } from "~/hooks/useBundles";
import useSkinBundles from "~/hooks/useSkinBundles";

const Bundles = () => {
  const intl = useIntl();
  const { opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity({
    id: opportunityId as string,
  });
  const { bundles, isLoading, selectedBundles } = useBundles();
  const { setSkinBundleAndHandleSkinCategory } = useSkinBundles();

  const groupedBundles = useMemo(() => {
    if (!bundles) return new Map<SalesforceBundleGroup, SalesforceBundle[]>();
    const filtered = bundles.filter(
      (bundle) => bundle.group !== SalesforceBundleGroup.Cosmetic // Will be shown on Cometics page
    );
    return filtered.reduce((acc, bundle) => {
      const group = bundle.group;
      if (!acc.has(group)) {
        acc.set(group, []);
      }
      acc.get(group)?.push(bundle);
      return acc;
    }, new Map<SalesforceBundleGroup, SalesforceBundle[]>());
  }, [bundles]);

  const availableGroups = useMemo(() => {
    return Array.from(groupedBundles.keys()).map((group) => ({
      key: group,
      value: group,
    }));
  }, [groupedBundles]);

  const [activeGroup, setActiveGroup] = useState<SalesforceBundleGroup | null>(
    null
  );

  useEffect(() => {
    if (availableGroups.length > 0 && !activeGroup) {
      setActiveGroup(availableGroups[0].key);
    }
  }, [availableGroups, activeGroup]);

  const filteredBundles = useMemo(() => {
    if (!activeGroup) return [];
    return groupedBundles.get(activeGroup) ?? [];
  }, [groupedBundles, activeGroup]);

  if (isLoading) {
    return (
      <div className="gap-header p-body flex flex-col h-full relative">
        <Header title={intl.formatMessage({ id: "bundles.title" })} />
        <div className="w-full h-full flex items-center justify-center">
          <SpinnerIcon />
        </div>
      </div>
    );
  }

  return (
    <div className="gap-header p-body flex flex-col h-full relative">
      <Header title={intl.formatMessage({ id: "bundles.title" })} />
      {availableGroups.length > 1 && (
        <TabsToggle
          options={availableGroups}
          activeOption={activeGroup}
          setActiveOption={setActiveGroup}
        />
      )}
      <div className="grid grid-cols-3 gap-6 w-full overflow-y-auto pb-10">
        {filteredBundles.map((bundle) => (
          <BundleCard
            key={bundle.id}
            bundle={bundle}
            onSelect={() => {
              setSkinBundleAndHandleSkinCategory(bundle);
            }}
            isSelected={selectedBundles.some(
              (selectedBundle) => selectedBundle.id === bundle.id
            )}
            isRecommended={false}
            disabled={!opportunity?.isValidForSales}
          />
        ))}
      </div>
    </div>
  );
};

export default Bundles;
